import { apiClient } from './client'
import type { MeasurementUnit } from '../measurements'

// Enums matching backend
export type GoalType = 'PopularHike' | 'TourOperatorHike' | 'GeneralFitness' | 'CustomHike'
export type ExerciseCategory = 'Elevation' | 'Strength' | 'Balance' | 'HIIT' | 'Cardio' | 'Flexibility' | 'Recovery'
export type ElevationType = 'TrueElevation' | 'ArtificialElevation' | 'None'
export type TrainingWeekFlag = 'CompressedTimeline' | 'PeakWeek' | 'TaperWeek' | 'KneeAccommodation' | 'AltitudePrep'

// Onboarding enums
export type EnduranceFrequency = 'FourPlusDays' | 'TwoThreeDays' | 'OneDayOrLess' | 'Occasionally' | 'NotAtAll'
export type HikingFrequency = 'ThreePlusDays' | 'OneTwoDays' | 'OneThreeDaysMonth' | 'FewTimes' | 'NotAtAll'
export type TrueElevationWorkoutAccess = 'Stairs' | 'StepUps' | 'LocalHikesOrTrails' | 'Hills'
export type EquipmentAccessOption = 'TreadmillWithIncline' | 'StairMachine'
export type HikeConcern = 'Endurance' | 'Joints' | 'Altitude' | 'Strength' | 'Other'

// Request types
export type TrainingPlanRequestDto = {
  userId: string
  goalType: GoalType
  goalId: string
  goalHikeStartDate?: string | null
  overridePackWeightKg?: number | null
  customDurationWeeks?: number | null
  disableTtlDurabilityCap?: boolean
}

export type AgeRange = 'Range18To29' | 'Range30To39' | 'Range40To49' | 'Range50Plus'

export type MockUserProfileDto = {
  ageRange?: AgeRange | null
  trainingDaysPerWeek?: number | null
  preferredDays?: string[] | null
  enduranceFrequency?: EnduranceFrequency | null
  hikingFrequency?: HikingFrequency | null
  trueElevationAccess?: TrueElevationWorkoutAccess[] | null
  equipmentAccess?: EquipmentAccessOption[] | null
  concerns?: HikeConcern[] | null
  measurementUnit?: MeasurementUnit | null
}

export type TestPlanGenerationRequest = {
  request: TrainingPlanRequestDto
  configOverride?: PlanGenerationConfigDto | null
  mockProfile?: MockUserProfileDto | null
}

// Response types
export type WorkoutExerciseDto = {
  code: string
  name: string
  category: ExerciseCategory
  order: number
  sets?: number | null
  reps?: number | null
  durationMinutes?: number | null
  distanceKm?: number | null
  elevationGainMeters?: number | null
  usePack: boolean
  notes?: string | null
}

export type WorkoutDayDto = {
  date: string
  dayOfWeek: string
  isMilestone: boolean
  isLongWorkout: boolean
  distanceKm: number
  elevationGainMeters: number
  packWeightKg: number
  elevationType: ElevationType
  exercises: WorkoutExerciseDto[]
  estimatedDurationMinutes: number
  notes?: string | null
}

export type CoachNoteDto = {
  createdAt: string
  reason: string
  message: string
  weekNumber?: number | null
  effectiveDate?: string | null
  rationale?: Record<string, unknown> | null
}

export type TrainingWeekDto = {
  weekNumber: number
  weekStartDate: string
  weekEndDate: string
  targetDistanceKm: number
  targetElevationGainMeters: number
  targetAveragePackWeightKg: number
  intensityFactor: number
  flags: TrainingWeekFlag[]
  workouts: WorkoutDayDto[]
  coachNotes: CoachNoteDto[]
  notes?: string | null
}

export type PlanTargetsDto = {
  totalDistanceKm: number
  totalElevationGainMeters: number
  targetPackWeightKg: number
}

export type GeneratedTrainingPlanDto = {
  id: string
  userId: string
  goalType: GoalType
  goalId: string
  goalName: string
  planStartDate: string
  planEndDate: string
  targetHikeDate?: string | null
  durationWeeks: number
  targets: PlanTargetsDto
  weeks: TrainingWeekDto[]
  warnings: string[]
  configVersion?: string | null
  createdAt: string
}

export type PlanGenerationSummaryDto = {
  totalPlanDistanceKm: number
  totalPlanElevationGainMeters: number
  targetDistanceKm: number
  targetElevationGainMeters: number
  distanceVariancePercent: number
  elevationVariancePercent: number
  totalWorkouts: number
  trueElevationWorkouts: number
  artificialElevationWorkouts: number
  packWorkouts: number
  noPackWorkouts: number
  averageWeeklyDistanceKm: number
  averageWeeklyElevationMeters: number
}

export type PlanGenerationTraceEntryDto = {
  weekNumber: number
  date?: string | null
  rule: string
  action: string
  detail: string
  contexts: PlanGenerationTraceContextDto[]
  targetWaterfall?: PlanTargetWaterfallDto | null
  packWeightResolution?: ResolvedPackWeightDto | null
}

export type ResolvedPackWeightDto = {
  valueKg: number
  sourceText?: string | null
  sourceUnit?: string | null
  assumedUnit?: string | null
  resolutionPath: string
  wasDefaulted: boolean
  wasCapped: boolean
  wasRoundedForDisplay: boolean
  isAmbiguousNumeric: boolean
  sourceValue?: number | null
  finalTargetKg?: number | null
  generatedMaxWorkoutPackKg?: number | null
  warning?: string | null
}

export type PlanGenerationTraceContextDto = {
  date?: string | null
  dayOfWeek?: string | null
  dayType?: string | null
  sessionType?: string | null
  reason?: string | null
  distanceKm?: number | null
  elevationGainMeters?: number | null
  packWeightKg?: number | null
  exercises: string[]
  summary: string
}

export type WeeklyTargetWaterfallStageDto = {
  weekNumber: number
  phase: string
  flags: string[]
  targetDistanceKm: number
  targetElevationMeters: number
  targetAveragePackWeightKg: number
  workoutDistanceKm?: number | null
  workoutElevationMeters?: number | null
  maxWorkoutPackKg?: number | null
  loadDayCount?: number | null
  trainingDayCount?: number | null
  restDayCount?: number | null
  milestoneWorkoutCount?: number | null
  protectedWindowWorkoutCount?: number | null
  notes: string[]
  ruleIds: string[]
}

export type PlanTargetWaterfallStageDto = {
  stageId: string
  stageName: string
  sequence: number
  totalWeekTargetDistanceKm: number
  totalWeekTargetElevationMeters: number
  maxWeekTargetPackWeightKg: number
  totalWorkoutDistanceKm?: number | null
  totalWorkoutElevationMeters?: number | null
  maxWorkoutPackWeightKg?: number | null
  distanceDeltaFromPreviousKm: number
  elevationDeltaFromPreviousMeters: number
  packDeltaFromPreviousKg: number
  distanceDebtFromOriginalKm: number
  elevationDebtFromOriginalMeters: number
  notes: string[]
  ruleIds: string[]
  weeks: WeeklyTargetWaterfallStageDto[]
}

export type PlanTargetWaterfallDto = {
  originalDistanceKm: number
  originalElevationMeters: number
  originalTargetPackWeightKg: number
  stages: PlanTargetWaterfallStageDto[]
  finalWorkoutDistanceKm: number
  finalWorkoutElevationMeters: number
  finalMaxPackWeightKg: number
  distanceDebtKm: number
  elevationDebtMeters: number
  distanceVarianceFraction: number
  elevationVarianceFraction: number
}

export type PlanTargetComparisonDto = {
  rawGoalDistanceKm: number
  rawGoalElevationMeters: number
  rawGoalPackWeightKg: number
  feasiblePlanDistanceKm: number
  feasiblePlanElevationMeters: number
  feasiblePlanPackWeightKg: number
  finalWorkoutDistanceKm: number
  finalWorkoutElevationMeters: number
  finalMaxWorkoutPackWeightKg: number
  distanceDebtFromRawKm: number
  elevationDebtFromRawMeters: number
  distanceVarianceFromRawFraction: number
  elevationVarianceFromRawFraction: number
  distanceDeltaFromFeasibleKm: number
  elevationDeltaFromFeasibleMeters: number
  distanceVarianceFromFeasibleFraction: number
  elevationVarianceFromFeasibleFraction: number
  packDeltaFromRawKg: number
  packVarianceFromRawFraction: number
  packDeltaFromFeasibleKg: number
  packVarianceFromFeasibleFraction: number
  durabilityDistanceDebtCreatedKm: number
  durabilityElevationDebtCreatedMeters: number
  durabilityDistanceDebtRepaidKm: number
  durabilityElevationDebtRepaidMeters: number
  durabilityDistanceDebtUnpaidKm: number
  durabilityElevationDebtUnpaidMeters: number
  milestoneDistanceDebtCreatedKm: number
  milestoneElevationDebtCreatedMeters: number
  milestoneDistanceDebtRepaidKm: number
  milestoneElevationDebtRepaidMeters: number
  milestoneDistanceDebtUnpaidKm: number
  milestoneElevationDebtUnpaidMeters: number
  concentrationElevationDebtCreatedMeters?: number | null
  concentrationElevationDebtUnpaidMeters?: number | null
  warnings: string[]
}

export type TestPlanGenerationResponse = {
  success: boolean
  errorMessage?: string | null
  warnings: string[]
  plan?: GeneratedTrainingPlanDto | null
  summary?: PlanGenerationSummaryDto | null
  targetWaterfall?: PlanTargetWaterfallDto | null
  targetComparison?: PlanTargetComparisonDto | null
  targetMultiplierDiagnostics?: PlanTargetMultiplierDiagnosticsDto | null
  packWeightResolution?: ResolvedPackWeightDto | null
  trace?: PlanGenerationTraceEntryDto[]
}

export type PlanTargetMultiplierDiagnosticsDto = {
  difficultyLevel: number
  baseDistanceDayCount: number
  baseElevationDayCount: number
  baseDistanceKm: number
  baseElevationMeters: number
  configuredDistanceMultiplier: number
  configuredElevationMultiplier: number
  appliedDistanceMultiplier: number
  appliedElevationMultiplier: number
  maxAltitudeMeters: number
  highAltitudeThresholdMeters: number
  highAltitudeMinimumMultiplier: number
  highAltitudeElevationFloorEligible: boolean
  highAltitudeElevationFloorApplied: boolean
}

export type CohortWeeklyMinimumsDto = {
  ageThresholdYears: number
  under40MinWeeklyDistanceKm: number
  under40MinWeeklyElevationMeters: number
  over40MinWeeklyDistanceKm: number
  over40MinWeeklyElevationMeters: number
}

export type DifficultyMultiplierTableDto = {
  level1Distance: number
  level1Elevation: number
  level2Distance: number
  level2Elevation: number
  level3Distance: number
  level3Elevation: number
  level4Distance: number
  level4Elevation: number
}

export type WorkoutGradeCapTableDto = {
  level1MaxElevationMetersPerKm: number
  level2MaxElevationMetersPerKm: number
  level3MaxElevationMetersPerKm: number
  level4MaxElevationMetersPerKm: number
}

export type MilestoneSplitConfigDto = {
  twoWorkoutMaxElevationMeters: number
  threeWorkoutMaxElevationMeters: number
  leadWorkoutFraction: number
}

export type DurabilityTtlWeightsDto = {
  o40Weight: number
  elevationWeight: number
  packWeight: number
}

export type DurabilityWeekOverWeekCapsDto = {
  defaultTtlMaxIncreaseFraction: number
  under40JointTtlMaxIncreaseFraction: number
  age40PlusTtlMaxIncreaseFraction: number
  age40PlusJointTtlMaxIncreaseFraction: number
  elevationMaxIncreaseFraction: number
  packStepMaxIncreaseFraction: number
  elevationDebtSpikePreviousWeekMaxIncreaseFraction: number
  protectiveElevationDebtSpikePreviousWeekMaxIncreaseFraction: number
  elevationDebtSpikeOriginalWeekMaxIncreaseFraction: number
  protectiveElevationDebtSpikeOriginalWeekMaxIncreaseFraction: number
}

export type PlanGenerationConfigDto = {
  id?: string | null
  version: string
  name: string
  isActive: boolean
  weeklyMinDistanceKm: number
  weeklyMaxDistanceKm: number
  weeklyMinElevationGainMeters: number
  weeklyMaxElevationGainMeters: number
  minimumWorkoutDaysPerWeek: number
  weekDistributionCurve: number[]
  cohortWeeklyMinimums: CohortWeeklyMinimumsDto
  difficultyMultipliers: DifficultyMultiplierTableDto
  milestoneMinimumWeek: number
  milestoneSplits: MilestoneSplitConfigDto
  treadmillMaxDistanceKm: number
  maxNonMilestoneWorkoutElevationMeters: number
  maxNonMilestoneWorkoutElevationMetersForProtectiveBias: number
  treadmillMaxInclinePercent: number
  gradeCaps: WorkoutGradeCapTableDto
  trueElevationMinFractionDefault: number
  trueElevationMinFractionKneeIssues: number
  packWorkoutFractionDefault: number
  packWorkoutFractionKneeIssues: number
  durabilityTtlWeights: DurabilityTtlWeightsDto
  durabilityWeekOverWeekCaps: DurabilityWeekOverWeekCapsDto
  defaultPlanDurationWeeks: number
  minimumRecommendedWeeks: number
  topDistanceDaysCount: number
  topElevationDaysCount: number
  notes?: string | null
  createdAt?: string | null
  updatedAt?: string | null
  createdBy?: string | null
}

// Default values matching backend PlanGenerationConfig defaults.
// Used as fallbacks when editing configs saved before these fields existed.
export const DEFAULT_PLAN_LIMITS = {
  minimumWorkoutDaysPerWeek: 4,
  treadmillMaxDistanceKm: 16.093,
  maxNonMilestoneWorkoutElevationMeters: 1524,
  maxNonMilestoneWorkoutElevationMetersForProtectiveBias: 1524
} as const

export const DEFAULT_WEEK_DISTRIBUTION_CURVE: number[] = [
  0.70, 0.85, 0.90, 0.95, 1.00, 1.05, 1.15, 1.25, 1.35, 1.40, 0.85, 0.70
]

export const DEFAULT_COHORT_WEEKLY_MINIMUMS: CohortWeeklyMinimumsDto = {
  ageThresholdYears: 40,
  under40MinWeeklyDistanceKm: 14.5,
  under40MinWeeklyElevationMeters: 579,
  over40MinWeeklyDistanceKm: 11.3,
  over40MinWeeklyElevationMeters: 427
}

export const DEFAULT_DIFFICULTY_MULTIPLIERS: DifficultyMultiplierTableDto = {
  level1Distance: 4.1,
  level1Elevation: 3.0,
  level2Distance: 4.8,
  level2Elevation: 3.5,
  level3Distance: 3.5,
  level3Elevation: 4.3,
  level4Distance: 4.1,
  level4Elevation: 5.0
}

export const DEFAULT_GRADE_CAPS: WorkoutGradeCapTableDto = {
  level1MaxElevationMetersPerKm: 105,
  level2MaxElevationMetersPerKm: 130,
  level3MaxElevationMetersPerKm: 160,
  level4MaxElevationMetersPerKm: 190
}

export const DEFAULT_MILESTONE_SPLITS: MilestoneSplitConfigDto = {
  twoWorkoutMaxElevationMeters: 609,
  threeWorkoutMaxElevationMeters: 1067,
  leadWorkoutFraction: 0.30
}

export const DEFAULT_DURABILITY_TTL_WEIGHTS: DurabilityTtlWeightsDto = {
  o40Weight: 0.45,
  elevationWeight: 0.35,
  packWeight: 0.20
}

export const DEFAULT_DURABILITY_WEEK_OVER_WEEK_CAPS: DurabilityWeekOverWeekCapsDto = {
  defaultTtlMaxIncreaseFraction: 0.25,
  under40JointTtlMaxIncreaseFraction: 0.20,
  age40PlusTtlMaxIncreaseFraction: 0.20,
  age40PlusJointTtlMaxIncreaseFraction: 0.15,
  elevationMaxIncreaseFraction: 0.20,
  packStepMaxIncreaseFraction: 0.10,
  elevationDebtSpikePreviousWeekMaxIncreaseFraction: 0.50,
  protectiveElevationDebtSpikePreviousWeekMaxIncreaseFraction: 0.35,
  elevationDebtSpikeOriginalWeekMaxIncreaseFraction: 0.25,
  protectiveElevationDebtSpikeOriginalWeekMaxIncreaseFraction: 0.20
}

/**
 * Fills any missing/partial fields with backend defaults so configs saved
 * before these tunables existed can be safely edited without crashing.
 */
export function withPlanConfigDefaults(config: PlanGenerationConfigDto): PlanGenerationConfigDto {
  return {
    ...config,
    weekDistributionCurve: config.weekDistributionCurve?.length
      ? [...config.weekDistributionCurve]
      : [...DEFAULT_WEEK_DISTRIBUTION_CURVE],
    cohortWeeklyMinimums: { ...DEFAULT_COHORT_WEEKLY_MINIMUMS, ...(config.cohortWeeklyMinimums ?? {}) },
    difficultyMultipliers: { ...DEFAULT_DIFFICULTY_MULTIPLIERS, ...(config.difficultyMultipliers ?? {}) },
    milestoneMinimumWeek: config.milestoneMinimumWeek ?? 4,
    minimumWorkoutDaysPerWeek: config.minimumWorkoutDaysPerWeek ?? DEFAULT_PLAN_LIMITS.minimumWorkoutDaysPerWeek,
    milestoneSplits: { ...DEFAULT_MILESTONE_SPLITS, ...(config.milestoneSplits ?? {}) },
    treadmillMaxDistanceKm: config.treadmillMaxDistanceKm ?? DEFAULT_PLAN_LIMITS.treadmillMaxDistanceKm,
    maxNonMilestoneWorkoutElevationMeters: config.maxNonMilestoneWorkoutElevationMeters ?? DEFAULT_PLAN_LIMITS.maxNonMilestoneWorkoutElevationMeters,
    maxNonMilestoneWorkoutElevationMetersForProtectiveBias: config.maxNonMilestoneWorkoutElevationMetersForProtectiveBias ?? DEFAULT_PLAN_LIMITS.maxNonMilestoneWorkoutElevationMetersForProtectiveBias,
    treadmillMaxInclinePercent: config.treadmillMaxInclinePercent ?? 12,
    gradeCaps: { ...DEFAULT_GRADE_CAPS, ...(config.gradeCaps ?? {}) },
    durabilityTtlWeights: { ...DEFAULT_DURABILITY_TTL_WEIGHTS, ...(config.durabilityTtlWeights ?? {}) },
    durabilityWeekOverWeekCaps: { ...DEFAULT_DURABILITY_WEEK_OVER_WEEK_CAPS, ...(config.durabilityWeekOverWeekCaps ?? {}) }
  }
}

// API functions
export async function testPlanGeneration(request: TestPlanGenerationRequest): Promise<TestPlanGenerationResponse> {
  const { data } = await apiClient.post<TestPlanGenerationResponse>('/api/admin/plan-generation/test', request)
  return data
}

export async function getActiveConfig(): Promise<PlanGenerationConfigDto> {
  const { data } = await apiClient.get<PlanGenerationConfigDto>('/api/admin/plan-generation/config')
  return data
}

export async function getAllConfigs(): Promise<PlanGenerationConfigDto[]> {
  const { data } = await apiClient.get<PlanGenerationConfigDto[]>('/api/admin/plan-generation/configs')
  return data
}

export async function getDefaultConfig(): Promise<PlanGenerationConfigDto> {
  const { data } = await apiClient.get<PlanGenerationConfigDto>('/api/admin/plan-generation/config/defaults')
  return data
}

export async function createConfig(config: PlanGenerationConfigDto): Promise<PlanGenerationConfigDto> {
  const { data } = await apiClient.post<PlanGenerationConfigDto>('/api/admin/plan-generation/config', config)
  return data
}

export async function updateConfig(id: string, config: PlanGenerationConfigDto): Promise<PlanGenerationConfigDto> {
  const { data } = await apiClient.put<PlanGenerationConfigDto>(`/api/admin/plan-generation/config/${id}`, config)
  return data
}

export async function activateConfig(id: string): Promise<PlanGenerationConfigDto> {
  const { data } = await apiClient.post<PlanGenerationConfigDto>(`/api/admin/plan-generation/config/${id}/activate`)
  return data
}

export async function deleteConfig(id: string): Promise<void> {
  await apiClient.delete(`/api/admin/plan-generation/config/${id}`)
}
